import { useState } from "react";
import { Sparkle, Copy, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../../lib/teleproApi";

/**
 * PersonalizedPitchCard — Vague 11-B.
 *
 * Small collapsed card at the top of AssistPanel. Click « Générer pitch IA »,
 * we call /ai/pitch/{biz.id} (cached 24h) and render 3 tailored lines :
 *   1. Hook — accroche locale crédible
 *   2. Pitch — adapté à la situation (site vs pas de site, chaîne vs indé)
 *   3. Question — pour engager la conversation
 *
 * Each line has its own copy button so the télépro can grab exactly the
 * part they need.
 */
export default function PersonalizedPitchCard({ biz }) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [copiedIdx, setCopiedIdx] = useState(null);

  const generate = async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const r = await teleproApi.get(`/ai/pitch/${biz.id}`);
      if (r?.data?.hook || r?.data?.pitch) {
        setState({ loading: false, data: r.data, error: null });
      } else {
        setState({ loading: false, data: null, error: "L'IA n'a rien retourné" });
      }
    } catch (e) {
      setState({ loading: false, data: null, error: e.message });
    }
  };

  const copyLine = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1200);
      toast.success("Copié");
    } catch (_) { /* noop */ }
  };

  if (!state.data && !state.loading) {
    return (
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 border border-primary/20 rounded-sm p-3 mb-3" data-testid="pitch-ai-card-collapsed">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-primary">
            <Sparkle size={12} weight="fill" className="text-yellow-500"/>
            <span className="font-semibold">Pitch sur mesure pour ce prospect</span>
          </div>
          <button
            onClick={generate}
            className="text-[11px] font-bold bg-primary text-primary-foreground hover:bg-[#0f3d29] rounded-sm px-2.5 py-1"
            data-testid="pitch-ai-generate"
          >
            Générer avec l'IA ✨
          </button>
        </div>
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className="bg-primary/5 border border-primary/20 rounded-sm p-3 mb-3 text-xs text-muted-foreground italic" data-testid="pitch-ai-loading">
        <Sparkle size={11} weight="fill" className="inline text-yellow-500 mr-1"/>
        L&apos;IA prépare un pitch adapté à <b>{biz.name}</b>…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="bg-red-500/5 border border-red-500/30 rounded-sm p-3 mb-3 text-xs text-red-800 flex items-center justify-between gap-2" data-testid="pitch-ai-error">
        <span>❌ {state.error}</span>
        <button onClick={generate} className="text-[11px] font-semibold text-red-700 underline">Réessayer</button>
      </div>
    );
  }

  const { hook, pitch, question, cached } = state.data;
  const lines = [
    { label: "Accroche", text: hook },
    { label: "Pitch", text: pitch },
    { label: "Question", text: question },
  ].filter((l) => l.text);

  return (
    <div className="bg-primary/5 border border-primary/30 rounded-sm p-3 mb-3 space-y-2" data-testid="pitch-ai-card">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-primary">
        <Sparkle size={11} weight="fill" className="text-yellow-500"/>
        Pitch personnalisé{cached ? " · cache" : " · IA"}
      </div>
      {lines.map((line, idx) => (
        <div key={idx} className="flex items-start gap-1.5 group">
          <span className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground shrink-0 mt-0.5 w-14">
            {line.label}
          </span>
          <span className="text-xs leading-snug flex-1">{line.text}</span>
          <button
            onClick={() => copyLine(line.text, idx)}
            className="opacity-40 hover:opacity-100 shrink-0 text-muted-foreground hover:text-primary"
            data-testid={`pitch-ai-copy-${idx}`}
            title="Copier"
          >
            {copiedIdx === idx ? <Check size={11} weight="bold" className="text-primary"/> : <Copy size={11}/>}
          </button>
        </div>
      ))}
      <button
        onClick={generate}
        className="text-[10px] text-muted-foreground hover:text-primary underline"
        data-testid="pitch-ai-regenerate"
      >
        Régénérer
      </button>
    </div>
  );
}
